import type {
  CommunicationPreferencesUpdateInput,
  ConsentHistoryPage,
  ConsentInitializationInput,
  CustomerProfile,
  CustomerProfileResult,
  EmailChangeConfirmationInput,
  EmailChangeRequestInput,
  EmailChangeResult,
  ProfileUpdateInput,
} from '@emazy/shared-types';
import {
  consentHistoryPageSchema,
  customerProfileResultSchema,
  customerProfileSchema,
} from '@emazy/shared-validation';

import { ApiClient } from '@/lib/api';

export class CustomerProfileApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'CustomerProfileApiError';
  }
}

export interface CustomerProfileApi {
  getCustomerProfile(): Promise<CustomerProfileResult>;
  updateCustomerProfile(input: ProfileUpdateInput): Promise<CustomerProfile>;
  updateCommunicationPreferences(
    input: CommunicationPreferencesUpdateInput,
  ): Promise<CustomerProfile>;
  initializeConsents(input: ConsentInitializationInput): Promise<void>;
  getConsentHistory(params?: { limit?: number; cursor?: string }): Promise<ConsentHistoryPage>;
  startEmailChange(input: EmailChangeRequestInput): Promise<EmailChangeResult>;
  confirmEmailChange(input: EmailChangeConfirmationInput): Promise<EmailChangeResult>;
}

type Envelope<T> = { data: T; requestId: string };
type ErrorEnvelope = {
  error?: { code?: string; message?: string; fieldErrors?: Record<string, string[]> };
};

async function body<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as Envelope<T> & ErrorEnvelope;
  if (!response.ok) {
    throw new CustomerProfileApiError(
      payload.error?.message ?? 'We could not update your account. Please try again.',
      payload.error?.code ?? 'request-failed',
      response.status,
      payload.error?.fieldErrors,
    );
  }
  return payload.data;
}

export class HttpCustomerProfileApi implements CustomerProfileApi {
  constructor(private readonly client: ApiClient) {}

  private request(path: string, init: RequestInit = {}) {
    const headers = new Headers(init.headers);
    if (init.body) headers.set('content-type', 'application/json');
    return this.client.request(path, { ...init, headers, auth: 'protected' });
  }

  async getCustomerProfile() {
    return customerProfileResultSchema.parse(await body(await this.request('/v1/me/profile')));
  }

  async updateCustomerProfile(input: ProfileUpdateInput) {
    return customerProfileSchema.parse(
      await body(
        await this.request('/v1/me/profile', { method: 'PUT', body: JSON.stringify(input) }),
      ),
    );
  }

  async updateCommunicationPreferences(input: CommunicationPreferencesUpdateInput) {
    return customerProfileSchema.parse(
      await body(
        await this.request('/v1/me/communication-preferences', {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      ),
    );
  }

  async initializeConsents(input: ConsentInitializationInput) {
    await body(
      await this.request('/v1/me/consents/initialize', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    );
  }

  async getConsentHistory(params: { limit?: number; cursor?: string } = {}) {
    const search = new URLSearchParams();
    if (params.limit) search.set('limit', String(params.limit));
    if (params.cursor) search.set('cursor', params.cursor);
    const suffix = search.size ? `?${search.toString()}` : '';
    return consentHistoryPageSchema.parse(
      await body(await this.request(`/v1/me/consents${suffix}`)),
    );
  }

  async startEmailChange(input: EmailChangeRequestInput) {
    return body<EmailChangeResult>(
      await this.request('/v1/me/email-change', {
        method: 'POST',
        body: JSON.stringify({ newEmail: input.newEmail.trim().toLowerCase() }),
      }),
    );
  }

  async confirmEmailChange(input: EmailChangeConfirmationInput) {
    const result = await body<EmailChangeResult>(
      await this.request('/v1/me/email-change/confirm', {
        method: 'POST',
        body: JSON.stringify({ ...input, newEmail: input.newEmail.trim().toLowerCase() }),
      }),
    );
    if (result.status === 'completed') customerProfileSchema.parse(result.profile);
    return result;
  }
}
